<style src="./style.scss">
</style>

<script lang="ts">
  import { onMount } from "svelte";

  import Icon from "svelte-icon";
  import { navigate, useLocation } from "svelte-navigator";
  import { query, mutation } from "svelte-apollo";

  import Button from "@components/button";
  import Header from "@components/header";
  import Footer from "@components/footer";
  import CreditCard from "@components/creditCard";
  import TextInput from "@components/textInput";
  import Spinner from "@components/spinner";
  import Toast from "@components/toast";
  import PaymentErrorModal from "@components/paymentErrorModal";

  import BackButton from "@assets/icons/back.svg";
  import LockIcon from "@assets/icons/lock.svg";
  import { getToken } from "@services/auth";
  import { isValidExpirationDate, formatCurrency } from "@utils";

  import { CHANGE_CREDIT_CARD_MUTATION } from "./changeCreditCard.mutation.ts";
  import { SUBSCRIPTION_QUERY } from "./subscription.query.ts";

  const location = useLocation();

  let isLoading = true;
  let expirationDate = "";
  let securityCode = "";
  let cardNumber;
  let holder = "";
  let paymentErrorModal = false;
  let paymentErrorModalMessage = "";
  let paymentInfoText;
  let subscription = {};
  let message = {
    error: "",
    visible: false,
    text: "",
    toastType: "",
  };

  const changeCreditCard = mutation(CHANGE_CREDIT_CARD_MUTATION);
  const subscriptionQuery = query(SUBSCRIPTION_QUERY, { skip: true });

  onMount(() => {
    const token = getToken();

    if (!token) {
      navigate("/auth", { state: { from: "/payment" } });
      return;
    }

    if ($location.state?.id) {
      isLoading = false;
      subscription = $location.state;

      const { status, amount, installments } = subscription;

      if (status === "PAYMENT_PENDING") {
        showPaymentPendingToast();
      }

      paymentInfoText = formatValue(amount, installments);
      return;
    }

    fetchSubscription();
  });

  async function fetchSubscription() {
    const { data } = await subscriptionQuery.refetch();

    subscription = data.subscription;

    if (subscription.status === "PAYMENT_PENDING") {
      showPaymentPendingToast();
    }
    isLoading = false;
  }

  function showPaymentPendingToast() {
    showMessage(
      "Há um problema de cobrança. Atualize os seus dados de cartão para que sua assinatura não seja cancelada.",
      "alert"
    );
  }

  function formatValue(amount, installments) {
    if (installments === 1) {
      return `Sua assinatura é mensal de R$ ${formatCurrency(amount)}`;
    }

    return `Sua assinatura é de ${installments}x de R$ ${formatCurrency(
      amount / installments
    )}`;
  }

  function tryAgain() {
    paymentErrorModal = false;
  }

  function showMessage(text: string, toastType: "alert" | "info") {
    message = {
      toastType,
      text,
      visible: true,
    };
  }

  function clearMessage() {
    message = { visible: false, text: "", toastType: "alert" };
  }

  function goBack(success: boolean) {
    navigate("/overview", {
      state: success
        ? {
            showToast: true,
            message:
              subscription.status === "PAYMENT_PENDING"
                ? "Seu cartão de crédito foi atualizado com sucesso, tentaremos cobrar o valor pendente novamente em alguns dias."
                : "Seu cartão de crédito foi atualizado com sucesso",
          }
        : {},
    });
  }

  async function save() {
    const splitedExpirationDate = expirationDate.split("/");

    if (splitedExpirationDate[1].length === 2) {
      expirationDate = splitedExpirationDate.join("/20");
    }

    if (!isValidExpirationDate(expirationDate)) {
      showMessage(
        "Cartão vencido, verifique a data de validade do cartão",
        "alert"
      );

      window.scrollTo(0, 0);
      return true;

      return;
    }

    clearMessage();
    isLoading = true;

    const { data, errors } = await changeCreditCard({
      variables: {
        input: { expirationDate, securityCode, cardNumber, holder },
      },
      errorPolicy: "all",
    });

    if (errors) {
      isLoading = false;
      paymentErrorModal = true;
      paymentErrorModalMessage = errors[0].message;

      return;
    }

    if (!data.changeCreditCard) {
      isLoading = false;
      paymentErrorModal = true;
      paymentErrorModalMessage = "network-error";

      return;
    }

    if (data.changeCreditCard) {
      goBack(true);

      return;
    }
  }
</script>

<Header />

<PaymentErrorModal
  isOpen="{paymentErrorModal}"
  error="{paymentErrorModalMessage}"
  tryAgain="{tryAgain}"
  onClose="{() => (paymentErrorModal = false)}" />
{#if isLoading}
  <Spinner />
{:else}
  <div class="payment">
    <div>
      <div class="payment__back">
        <div on:click="{() => goBack()}">
          <Icon data="{BackButton}" size="24px" stroke="transparent" />
          <div>Voltar</div>
        </div>
      </div>
      <div class="payment__content">
        <div>
          {#if message.visible}
            <div class="toast-container">
              <Toast
                message="{message.text}"
                toastType="{message.toastType}"
                visible="{message.visible}"
                onClose="{clearMessage}" />
            </div>
          {/if}

          <h1>Atualizar pagamento</h1>

          <p>
            {paymentInfoText}. A atualização de seus dados de pagamento não
            ativa, faz upgrade ou estende sua assinatura.
          </p>
          <div>
            <div>
              <form on:submit|preventDefault="{save}">
                <TextInput
                  label="Nome"
                  placeholder="Nome no cartão"
                  bind:value="{holder}"
                  autocomplete="cc-csc" />

                <CreditCard
                  bind:expirationDate
                  bind:securityCode
                  bind:cardNumber
                  finalValue="{subscription.cardNumber}" />

                <div class="payment__actions">
                  <Button
                    title="Salvar"
                    onClick="{save}"
                    type="secondary"
                    disabled="{!cardNumber ||
                      !expirationDate ||
                      !securityCode}" />
                  <div>
                    <Icon
                      data="{LockIcon}"
                      size="16px"
                      stroke="transparent"
                      fill="transparent" />
                    Pagamento seguro
                  </div>
                </div>
              </form>
            </div>

            <div>
              <p>
                A Go Tecnologia em Educação S.A. pode emitir uma autorização
                temporária no seu cartão de crédito como confirmação. Trata-se
                apenas de uma autorização e não uma cobrança. Talvez a sua
                bandeira de cartão de crédito informe sobre a autorização.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<div class="desktop-only">
  <Footer />
</div>
